import { isEmpty } from 'lodash';
import requester from 'utils/requester';
import api from 'config/api';
import i18n from 'i18n';
import { GRID_MAX_SIZE as ITEMS_PER_REQUEST } from 'config';
import { hideLoader, showLoader } from 'actions/loader';
import { toggleChannelInFavoriteStatus, resetAllChannelsData } from 'actions/channels';
import { resetAllDetailsData } from 'actions/details';
import { showToast } from 'actions/toast';
import { selectWatchlist } from 'reducers/watchlist/selectors';
import * as types from './types';
import { CONTENT_TYPES } from 'constants/content-types';
import Platform from 'platform';

const paramsByType = {
  catchup: {
    url: `${api.host}${api.endpoints.searchVod}`,
    appendParams: false,
    params: {
      f_userData: 'favorites',
      type: 'VOD_ASSET',
      'f_metadata.contentType': 'EPG_EVENT',
      f_deviceType: Platform.getDeviceType(),
      f_epgEventState: 'CATCHUP',
      searchMetadata: true,
      detailed: true,
      includeVod: true,
      size: ITEMS_PER_REQUEST,
    },
  },
  vod: {
    url: `${api.host}${api.endpoints.searchVod}`,
    appendParams: false,
    params: {
      f_userData: 'favorites',
      type: 'ALL',
      'f_metadata.contentType': 'MOVIE',
      f_deviceType: Platform.getDeviceType(),
      searchMetadata: true,
      detailed: true,
      includeVod: true,
      size: ITEMS_PER_REQUEST,
    },
  },
  channel: {
    url: `${api.host}${api.endpoints.searchChannel}`,
    params: {
      f_userData: 'favorites',
      detailed: true,
      includeVod: true,
      size: ITEMS_PER_REQUEST,
    },
  },
};

export const fetchWatchlist = (type) => async (dispatch) => {
  try {
    const queryParams = paramsByType[type];

    if (!queryParams) return;

    dispatch(showLoader());
    dispatch({ type: types.FETCH, payload: { type } });

    const { content } = await requester({
      method: 'get',
      ...queryParams,
    });

    dispatch({ type: types.FETCH_SUCCESS, payload: { content, type } });
    if (content.length < ITEMS_PER_REQUEST) {
      dispatch({ type: types.LOAD_EXTRA_ITEMS_COMPLETED, payload: { type } });
    }
  } catch (error) {
    console.error(error);
    dispatch({
      type: types.FETCH_ERROR,
      payload: { error, type },
    });
  } finally {
    dispatch(hideLoader());
  }
};

export const loadExtraWatchlistItems = (type) => async (dispatch, getState) => {
  try {
    const continueWatching = selectWatchlist(getState())[type];
    const { isOnLazyLoad, isAllItemsWereLoaded } = continueWatching;
    const page = continueWatching.page + 1;

    if (isOnLazyLoad || isAllItemsWereLoaded) return;

    const queryParams = paramsByType[type];

    dispatch({ type: types.LOAD_EXTRA_ITEMS, payload: { type } });

    const { content } = await requester({
      method: 'get',
      url: queryParams.url,
      params: { ...queryParams.params, page },
    });

    if (isEmpty(content)) {
      dispatch({ type: types.LOAD_EXTRA_ITEMS_COMPLETED, payload: { type } });
    } else {
      dispatch({
        type: types.LOAD_EXTRA_ITEMS_SUCCESS,
        payload: { type, content, page },
      });
    }
  } catch (error) {
    console.error(error);
    dispatch({ type: types.LOAD_EXTRA_ITEMS_ERROR, payload: { type, error } });
  }
};

export const removeItem =
  ({ type, item }) =>
  async (dispatch) => {
    try {
      const { id } = item;
      const apiEndPointByAssetType =
        item.type === CONTENT_TYPES.TV_SERIES
          ? api.endpoints.favoritesSeries
          : api.endpoints.favorites;

      if (type === 'channel') {
        dispatch(toggleChannelInFavoriteStatus(id));
      } else {
        // Need to clean up all vod/catchu-p data if we mutate some item favorite status
        // It allow us to not think about EPG/Details cache
        dispatch(type === 'catchup' ? resetAllChannelsData() : resetAllDetailsData());

        await requester({
          method: 'delete',
          url: `${api.host}${apiEndPointByAssetType}/${id}`,
          data: { vodAssetId: id },
        });
        const toastText =
          type === 'vod'
            ? i18n.t('common.toast.videoFavoriteRemoved')
            : i18n.t('common.toast.programFavoriteRemoved');
        dispatch(showToast({ options: { toastText } }));
      }
      dispatch({ type: types.REMOVE_ITEM, payload: { type, id } });
    } catch (error) {
      console.error(error);
    }
  };

export const resetAllWatchlistData = () => ({ type: types.RESET_ALL_DATA });
